import { ColumnDef } from '@tanstack/react-table';
import i18n from 'i18next';
import { AlertTriangle } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { Trans } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { SingleSelect, SingleSelectOption } from '@/modules/common';
import { mapRateSourceToDTO } from '@/modules/common/mappers/rate-source.mapper';
import { RateSource } from '@/modules/common/types/rate-source';
import { getRateSourceLabel, NULL_OPTION_KEY } from '@/modules/common/utils/rate-source.utils';
import { ProjectConflictResolutionContext } from '@/modules/projects/types/project-conflict-resolution-context';
import {
  ConflictFieldConfig,
  ConflictFieldRow,
  ConflictSnapshot,
  PatchEffectAction,
} from '@/modules/projects/types/project-conflicts';
import { ProjectBillingParentFieldKey } from '@/modules/projects/utils/change-conflicts/project-billing-conflict-config';
import { formatDateRange } from '@/utils/date-formatters';

import { EntityAvatar } from '../../common/components/molecules/avatar/entity-avatar';

const MAX_VISIBLE_ITEMS = 3;

/**
 * Splits a comma-separated label into items.
 * Returns { visible, overflow } where overflow contains items beyond MAX_VISIBLE_ITEMS.
 */
const splitLabelItems = (label: string): { visible: string[]; overflow: string[] } => {
  const items = label.split(', ');
  if (items.length <= MAX_VISIBLE_ITEMS) return { visible: items, overflow: [] };
  return {
    visible: items.slice(0, MAX_VISIBLE_ITEMS),
    overflow: items.slice(MAX_VISIBLE_ITEMS),
  };
};

/**
 * Truncates a comma-separated label to MAX_VISIBLE_ITEMS + "and N more".
 * Returns the original label if item count is within the limit.
 */
const truncateLabel = (label: string): string => {
  const { visible, overflow } = splitLabelItems(label);
  if (overflow.length === 0) return label;
  return `${visible.join(', ')} ${i18n.t('common:table.andNMore', { count: overflow.length })}`;
};

/**
 * Renders a comma-separated value with tooltip showing all items when >MAX_VISIBLE_ITEMS
 */
const renderWithOverflowTooltip = (label: string): React.ReactNode => {
  const { overflow } = splitLabelItems(label);
  const truncated = truncateLabel(label);

  if (overflow.length === 0) {
    return <span>{label}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-default">{truncated}</span>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent>
          <p className="max-w-xs text-sm">{label}</p>
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
};

/**
 * Renders the attribute cell content
 * For sub-rows (e.g., team members), shows avatar + name
 * For regular rows, shows the field label
 */
const renderAttributeCell = (row: ConflictFieldRow): React.ReactNode => {
  if (row.isSubRow && row.subRowData) {
    const { label, avatarUrl } = row.subRowData;

    return (
      <div className="flex items-center gap-2">
        <EntityAvatar image={avatarUrl} name={label} size="xs" shape="rounded" />
        <span className="text-sm">{label}</span>
      </div>
    );
  }

  return <span className="font-medium">{row.fieldLabel}</span>;
};

/**
 * Checks if the action requires showing a warning icon
 * Only CUT and DELETE actions should show the warning
 */
const shouldShowWarning = (action: PatchEffectAction | string): boolean => {
  const actionLower = typeof action === 'string' ? action.toLowerCase() : action;
  return actionLower === PatchEffectAction.Cut || actionLower === PatchEffectAction.Delete;
};

/**
 * Renders the tooltip content for patch effect actions
 * Returns JSX with proper formatting based on action type
 */
const renderPatchEffectTooltip = (snapshot: ConflictSnapshot): React.ReactNode => {
  const actionLower =
    typeof snapshot.patchEffect.action === 'string'
      ? snapshot.patchEffect.action.toLowerCase()
      : snapshot.patchEffect.action;

  if (actionLower === PatchEffectAction.Delete || actionLower === 'delete') {
    return (
      <Trans
        i18nKey="common:conflictResolution.patchEffect.deleteDescription"
        components={{ bold: <span className="font-semibold" /> }}
      />
    );
  }

  if (actionLower === PatchEffectAction.Cut || actionLower === 'cut') {
    const dateRange = formatDateRange(
      snapshot.patchEffect.cutEffectiveStartDate || '',
      snapshot.patchEffect.cutEffectiveEndDate
    );

    return (
      <Trans
        i18nKey="common:conflictResolution.patchEffect.cutDescription"
        values={{ dateRange }}
        components={{ bold: <span className="font-semibold" /> }}
      />
    );
  }

  return null;
};

export interface CreateConflictColumnsParams<TFieldKey extends string> {
  snapshots: ConflictSnapshot[];
  selectedDateRange: { from: Date; to?: Date | null };
  fields: ConflictFieldConfig<TFieldKey>[];
  form: UseFormReturn<Record<string, string>>;
  context?: ProjectConflictResolutionContext;
  /** Current form values from the parent form — used as fallback for fields not present in the conflict form (e.g. status when only probability conflicts) */
  currentFormValues?: Record<string, unknown>;
}

/**
 * Formats a value for display in the table
 * Uses the field's custom formatter if provided, passing optional context
 */
const formatValue = (
  value: unknown,
  field?: ConflictFieldConfig<string>,
  context?: ProjectConflictResolutionContext
): string => {
  if (value === null || value === undefined) {
    return i18n.t('common:table.noValue');
  }

  if (field?.valueFormatter) {
    return field.valueFormatter(value, context);
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  // Handle objects
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

/**
 * Gets the field config for a row
 * For sub-rows, uses the fieldConfig attached to the row
 * For regular rows, finds it from the fields array
 */
const getFieldConfig = <TFieldKey extends string>(
  row: ConflictFieldRow,
  fields: ConflictFieldConfig<TFieldKey>[]
): ConflictFieldConfig<TFieldKey> | undefined => {
  if (row.isSubRow && row.fieldConfig) {
    return row.fieldConfig as ConflictFieldConfig<TFieldKey>;
  }
  return fields.find(f => f.key === row.fieldKey);
};

/**
 * Normalizes a value for comparison using field's comparison function
 * This ensures values in different formats (e.g., app enum vs DTO) are treated as equal
 * For deduplication in dropdowns, we use snapshot comparison for all values
 */
const normalizeValueForComparison = (
  value: unknown,
  field?: ConflictFieldConfig<string>
): unknown => {
  // Handle null/undefined values consistently
  if (value === null || value === undefined) {
    return null;
  }

  if (!field) return value;

  // For dropdown deduplication, always use the same normalization function
  // Prefer snapshot comparison if available, otherwise use form comparison
  if (field.getSnapshotValueForComparison) {
    const normalized = field.getSnapshotValueForComparison(value);
    return normalized === null || normalized === undefined ? null : normalized;
  }

  if (field.getFormValueForComparison) {
    const normalized = field.getFormValueForComparison(value);
    return normalized === null || normalized === undefined ? null : normalized;
  }

  return value;
};

/**
 * Creates options for the dropdown selector
 * Includes current value and all unique snapshot values
 * Deduplicates by normalized value to handle different formats (app enum vs DTO)
 *
 * Special handling for conditional fields:
 * - flatRate: Only includes snapshot values where snapshot's rateSource is FLAT
 * - userRates sub-rows: Collects unique rate sources from current value and snapshots
 */
const createOptions = (
  row: ConflictFieldRow,
  field?: ConflictFieldConfig<string>,
  context?: ProjectConflictResolutionContext,
  snapshots?: ConflictSnapshot[]
): SingleSelectOption[] => {
  // Special handling for userRates sub-rows when rate source is RATE_CARD
  const isUserRatesRow =
    row.isSubRow && row.parentFieldKey === ProjectBillingParentFieldKey.UserRates;

  if (isUserRatesRow && context?.rateSource === RateSource.ProjectRateCard) {
    const optionsMap = new Map<
      RateSource | typeof NULL_OPTION_KEY,
      { rateSource: string; rate: number } | null
    >();

    const addUserRateOption = (value: unknown, fromSnapshot = false) => {
      if (value === null) {
        optionsMap.set(NULL_OPTION_KEY, null);
        return;
      }

      if (
        typeof value === 'object' &&
        value !== null &&
        'rateSource' in value &&
        typeof value.rateSource === 'string'
      ) {
        const rateSource = value.rateSource as RateSource;

        // Flat Rate only makes sense for non-billable periods. When the resolved
        // rate source is not flat (e.g. Project Rate Card), snapshot values that
        // carry Flat Rate come from non-billable conflicting ranges and should not
        // be offered as selectable options for individual members.
        if (fromSnapshot && rateSource === RateSource.FlatRate) {
          return;
        }

        if (!optionsMap.has(rateSource)) {
          optionsMap.set(rateSource, {
            rateSource: mapRateSourceToDTO(rateSource),
            rate: 'rate' in value && typeof value.rate === 'number' ? value.rate : 0,
          });
        }
      }
    };

    // Keep both the reactive selected value and the original current value
    // so switching a member back to the previous rate source does not remove it
    // from the options list.
    addUserRateOption(row.selectedValue);
    addUserRateOption(row.currentValue);
    row.snapshotValues.forEach(value => addUserRateOption(value, true));

    return Array.from(optionsMap.entries()).map(([rateSource, value]) => {
      if (rateSource === NULL_OPTION_KEY) {
        return {
          value: JSON.stringify(null),
          label: i18n.t('common:table.noValue'),
        };
      }

      return {
        value: JSON.stringify(value),
        label: getRateSourceLabel(rateSource),
      };
    });
  }

  // Default behavior for all other fields
  const optionsMap = new Map<string, SingleSelectOption>();

  // Helper to create a unique key for deduplication
  const createDeduplicationKey = (value: unknown): string => {
    const normalized = normalizeValueForComparison(value, field);
    // JSON.stringify handles null/undefined consistently, returning 'null' and undefined respectively
    return JSON.stringify(normalized ?? null);
  };

  const addOptionIfAllowed = (value: unknown) => {
    if ((value === null || value === undefined) && field?.excludeNullOption) {
      return;
    }

    const optionKey = createDeduplicationKey(value);
    if (optionsMap.has(optionKey)) {
      return;
    }

    optionsMap.set(optionKey, {
      value: JSON.stringify(value),
      label: formatValue(value, field, context),
    });
  };

  // Add the reactive selected value first so sync rules can surface derived values,
  // but keep the original current value as an alternative selectable option.
  addOptionIfAllowed(row.selectedValue);
  addOptionIfAllowed(row.currentValue);

  // Add snapshot values - deduplicate by normalized value
  if (snapshots) {
    snapshots.forEach(snapshot => {
      const value = row.snapshotValues.get(snapshot.id);
      if (value !== undefined) {
        // Special filtering for flatRate field - only include if snapshot had FLAT rate source
        if (row.fieldKey === 'flatRate') {
          if (snapshot.data.rateSource !== RateSource.FlatRate) {
            return; // Skip this snapshot value
          }
        }

        // Skip null values when excludeNullOption is set
        if ((value === null || value === undefined) && field?.excludeNullOption) {
          return;
        }

        // Create deduplication key for snapshot value
        const valueKey = createDeduplicationKey(value);

        // Only add if we haven't seen this normalized value before
        if (!optionsMap.has(valueKey)) {
          // Format with current dynamic context (selected currency), not snapshot currency
          const valueStr = formatValue(value, field, context);
          optionsMap.set(valueKey, {
            value: JSON.stringify(value),
            label: valueStr,
          });
        }
      }
    });
  } else {
    // Fallback: format with global context if snapshots not provided
    row.snapshotValues.forEach(value => {
      const valueKey = createDeduplicationKey(value);
      if (!optionsMap.has(valueKey)) {
        const valueStr = formatValue(value, field, context);
        optionsMap.set(valueKey, {
          value: JSON.stringify(value),
          label: valueStr,
        });
      }
    });
  }

  return Array.from(optionsMap.values());
};

/**
 * Generates table columns for conflict resolution
 * Column widths are set to match Figma design
 */
export const createConflictColumns = <TFieldKey extends string>({
  snapshots,
  selectedDateRange,
  fields,
  form,
  context,
  currentFormValues,
}: CreateConflictColumnsParams<TFieldKey>): ColumnDef<ConflictFieldRow>[] => {
  return [
    // Column 1: Attribute name
    {
      id: 'attribute',
      header: () => (
        <div className="min-w-[120px] max-w-[160px]">
          {i18n.t('common:conflictResolution.attribute')}
        </div>
      ),
      size: 140,
      cell: ({ row }) => (
        <div className="min-w-[120px] max-w-[160px]">{renderAttributeCell(row.original)}</div>
      ),
    },

    // Column 2: Resulting value (dropdown)
    {
      id: 'resultingValue',
      header: () => (
        <div className="min-w-[180px] max-w-[240px]">
          <div className="font-medium">{i18n.t('common:conflictResolution.resultingValue')}</div>
          {selectedDateRange?.from && (
            <div className="text-xs font-normal text-muted-foreground">
              {formatDateRange(selectedDateRange.from, selectedDateRange.to)}
            </div>
          )}
        </div>
      ),
      size: 200,
      cell: ({ row }) => {
        const field = getFieldConfig(row.original, fields);
        const options = createOptions(row.original, field, context, snapshots);

        // Disable userRates selectors unless rate source is RATE_CARD
        const isUserRatesRow =
          row.original.isSubRow &&
          row.original.parentFieldKey === ProjectBillingParentFieldKey.UserRates;
        const isUserRatesDisabled =
          isUserRatesRow && context?.rateSource !== RateSource.ProjectRateCard;

        // Check field-level disable condition (e.g., probability disabled when status is ACTIVE).
        // Seed with currentFormValues so that fields not present in the conflict form
        // (e.g. status when only probability conflicts) still evaluate correctly.
        const conflictFormValues = form.getValues();
        const selectedValues: Record<string, unknown> = { ...(currentFormValues ?? {}) };
        Object.keys(conflictFormValues).forEach(key => {
          const jsonValue = conflictFormValues[key];
          try {
            selectedValues[key] = jsonValue ? JSON.parse(jsonValue) : null;
          } catch {
            selectedValues[key] = jsonValue;
          }
        });
        const isFieldDisabled = field?.isDisabled ? field.isDisabled(selectedValues) : false;

        const isDisabled = isUserRatesDisabled || isFieldDisabled;

        // When field is disabled, show a disabled SingleSelect with the current form value as the only option
        const resolvedOptions = isFieldDisabled
          ? (() => {
              const rawValue = form.getValues()[row.original.fieldKey as string];
              try {
                const parsed = rawValue ? JSON.parse(rawValue) : null;
                if (parsed === null || parsed === undefined) {
                  return [{ value: JSON.stringify(null), label: i18n.t('common:table.noValue') }];
                }
                return [{ value: rawValue, label: formatValue(parsed, field, context) }];
              } catch {
                return [{ value: JSON.stringify(null), label: i18n.t('common:table.noValue') }];
              }
            })()
          : options;

        return (
          <div className="min-w-[180px] max-w-[240px]">
            <SingleSelect
              name={row.original.fieldKey as string}
              control={form.control}
              options={resolvedOptions}
              placeholder={i18n.t('common:conflictResolution.selectValue')}
              searchable={false}
              disabled={isDisabled}
              isClearSelectionEnabled={false}
              renderTrigger={(selectedOption, placeholder) =>
                selectedOption ? (
                  <span className="truncate text-sm font-normal">
                    {truncateLabel(selectedOption.label)}
                  </span>
                ) : (
                  <span className="truncate text-sm font-normal text-muted-foreground">
                    {placeholder}
                  </span>
                )
              }
              renderOption={option => (
                <span className="block truncate text-sm">{truncateLabel(option.label)}</span>
              )}
            />
          </div>
        );
      },
    },

    // Dynamic columns for each conflicting snapshot
    ...snapshots.map((snapshot, index) => ({
      id: `snapshot-${index}`,
      header: () => {
        const showWarning = shouldShowWarning(snapshot.patchEffect.action);
        const tooltipContent = renderPatchEffectTooltip(snapshot);

        return (
          <div className="flex min-w-[180px] max-w-[220px] items-center gap-2">
            {showWarning && tooltipContent && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 cursor-help text-warning" />
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent>
                    <p className="max-w-xs text-sm">{tooltipContent}</p>
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            )}
            <div className="flex-1">
              <div className="font-medium">
                {i18n.t('common:conflictResolution.conflictingRange')}
              </div>
              <div className="text-xs font-normal text-muted-foreground">
                {formatDateRange(snapshot.effectiveStartDate, snapshot.effectiveEndDate)}
              </div>
            </div>
          </div>
        );
      },
      size: 180,
      cell: ({ row }) => {
        const value = row.original.snapshotValues.get(snapshot.id);
        const field = getFieldConfig(row.original, fields);

        // Create snapshot-specific context with currency from snapshot data (if available)
        // defaultCurrency is used by project snapshots; currency is used by team member snapshots
        const snapshotCurrencyCode =
          snapshot.data.defaultCurrency?.code ||
          snapshot.data.currency?.code ||
          context?.currency?.code;
        const snapshotContext: ProjectConflictResolutionContext = {
          ...context,
          currency: snapshotCurrencyCode,
        };

        return (
          <div className="min-w-[180px] max-w-[220px]">
            {field?.renderValue
              ? field.renderValue(value)
              : renderWithOverflowTooltip(formatValue(value, field, snapshotContext))}
          </div>
        );
      },
    })),
  ];
};
